import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getBusinesses } from "../lib/businessService";

const BusinessContext = createContext(null);

export function BusinessProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);

  const loadBusinesses = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getBusinesses();

      setBusinesses(data);

      setSelectedBusiness((currentSelected) => {
        if (currentSelected) {
          const updatedSelected = data.find(
            (business) => business.id === currentSelected.id,
          );

          if (updatedSelected) return updatedSelected;
        }

        return data.length > 0 ? data[0] : null;
      });
    } catch (err) {
      console.error("Unable to load businesses", err);

      setBusinesses([]);
      setSelectedBusiness(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  const hasBusinesses = useMemo(() => {
    return businesses.length > 0;
  }, [businesses]);

  const value = useMemo(
    () => ({
      loading,
      businesses,
      setBusinesses,
      selectedBusiness,
      setSelectedBusiness,
      hasBusinesses,
      refreshBusinesses: loadBusinesses,
    }),
    [loading, businesses, selectedBusiness, hasBusinesses, loadBusinesses],
  );

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

export function useBusiness() {
  const context = useContext(BusinessContext);

  if (!context) {
    throw new Error("useBusiness must be used inside a BusinessProvider");
  }

  return context;
}
